<template>
  <div class="achats-view p-4">
    <!-- En-tête de page principal -->
    <div class="header-container mb-4">
      <div class="d-flex align-items-center gap-3">
        <div class="header-icon-badge">
          <i class="fas fa-shopping-cart"></i>
        </div>
        <div>
          <h2 class="header-title mb-0">Achats & Entrées en stock</h2>
          <p class="header-subtitle mb-0">Gestion des factures fournisseurs et des entrées en stock</p>
        </div>
      </div>

      <button class="btn-create-facture" @click="showCreateModal = true">
        <i class="fas fa-plus me-2"></i>Nouvelle facture d'achat
      </button>
    </div>

    <!-- Cards 4 KPIs Horizontales (Blanc Épuré avec Icônes Colorées) -->
    <div class="kpis-row mb-4">
      <!-- KPI 1: Achats ce mois -->
      <div class="kpi-white-card">
        <div class="kpi-icon-circle icon-blue">
          <i class="fas fa-shopping-cart"></i>
        </div>
        <div class="kpi-data">
          <span class="kpi-label-muted">Achats ce mois (TTC)</span>
          <div class="kpi-main-number">{{ (kpis.totalAchatsMoisTTC || 0).toFixed(2).replace('.', ',') }} DT</div>
          <span class="kpi-growth-text"><i class="fas fa-arrow-up me-1"></i>0% par rapport au mois dernier</span>
        </div>
      </div>

      <!-- KPI 2: Factures ce mois -->
      <div class="kpi-white-card">
        <div class="kpi-icon-circle icon-green">
          <i class="fas fa-file-alt"></i>
        </div>
        <div class="kpi-data">
          <span class="kpi-label-muted">Factures ce mois</span>
          <div class="kpi-main-number">{{ kpis.nombreFacturesMois || 0 }}</div>
          <span class="kpi-sub-text">0 facture</span>
        </div>
      </div>

      <!-- KPI 3: Total historique -->
      <div class="kpi-white-card">
        <div class="kpi-icon-circle icon-purple">
          <i class="fas fa-chart-line"></i>
        </div>
        <div class="kpi-data">
          <span class="kpi-label-muted">Total historique</span>
          <div class="kpi-main-number">{{ kpis.totalToutesFactures || 0 }} facture{{ (kpis.totalToutesFactures > 1) ? 's' : '' }}</div>
          <span class="kpi-sub-text">Depuis le début</span>
        </div>
      </div>

      <!-- KPI 4: Fournisseurs actifs -->
      <div class="kpi-white-card">
        <div class="kpi-icon-circle icon-orange">
          <i class="fas fa-user-friends"></i>
        </div>
        <div class="kpi-data">
          <span class="kpi-label-muted">Fournisseurs actifs</span>
          <div class="kpi-main-number">{{ kpis.nombreFournisseurs || 0 }}</div>
          <span class="kpi-sub-text">Fournisseurs</span>
        </div>
      </div>
    </div>

    <!-- Ligne des Filtres (N° Facture, Fournisseur, Date début, Date fin, Réinitialiser) -->
    <div class="filters-bar-card mb-4">
      <div class="filter-field">
        <label class="filter-label">N° Facture</label>
        <div class="input-with-icon">
          <i class="fas fa-search input-icon"></i>
          <input
            type="text"
            class="form-control-pill"
            placeholder="Rechercher par N° facture.."
            v-model="filters.search"
            @input="fetchFactures"
          />
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">Fournisseur</label>
        <div class="select-wrapper">
          <select class="form-select-pill" v-model="filters.fournisseur" @change="fetchFactures">
            <option value="">Sélectionner un fournisseur..</option>
            <option v-for="f in uniqueFournisseurs" :key="f" :value="f">{{ f }}</option>
          </select>
        </div>
      </div>

      <div class="filter-field">
        <label class="filter-label">Date début</label>
        <input
          type="date"
          class="form-control-pill"
          v-model="filters.dateDebut"
          @change="fetchFactures"
        />
      </div>

      <div class="filter-field">
        <label class="filter-label">Date fin</label>
        <input
          type="date"
          class="form-control-pill"
          v-model="filters.dateFin"
          @change="fetchFactures"
        />
      </div>

      <div class="filter-field btn-reset-align">
        <button class="btn-reset-pill" @click="resetFilters">
          <i class="fas fa-redo-alt me-1"></i>Réinitialiser
        </button>
      </div>
    </div>

    <!-- Onglets des Statuts (Toutes, Brouillons, Validées, Payées, Impayées, Annulées) -->
    <div class="status-tabs-row mb-4">
      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Toutes' }"
        @click="selectTab('Toutes')"
      >
        Toutes <span class="tab-badge badge-green-light">{{ totalCount }}</span>
      </button>

      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Brouillons' }"
        @click="selectTab('Brouillons')"
      >
        Brouillons <span class="tab-badge badge-gray">{{ brouillonsCount }}</span>
      </button>

      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Validées' }"
        @click="selectTab('Validées')"
      >
        Validées <span class="tab-badge badge-green">{{ valideesCount }}</span>
      </button>

      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Payées' }"
        @click="selectTab('Payées')"
      >
        Payées <span class="tab-badge badge-blue">{{ payeesCount }}</span>
      </button>

      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Impayées' }"
        @click="selectTab('Impayées')"
      >
        Impayées <span class="tab-badge badge-orange">{{ impayeesCount }}</span>
      </button>

      <button
        class="tab-pill"
        :class="{ active: currentTab === 'Annulées' }"
        @click="selectTab('Annulées')"
      >
        Annulées <span class="tab-badge badge-red">{{ annuleesCount }}</span>
      </button>
    </div>

    <!-- Card Liste des Factures d'Achat -->
    <div class="main-table-card">
      <div class="table-header-row">
        <h4 class="table-card-title">Liste des factures d'achat</h4>
        <div class="d-flex align-items-center gap-3">
          <span class="text-muted small fw-semibold">{{ filteredFactures.length }} facture{{ filteredFactures.length > 1 ? 's' : '' }}</span>
          <button class="btn-exporter" @click="exportData">
            <i class="fas fa-download me-1"></i>Exporter <i class="fas fa-chevron-down ms-1"></i>
          </button>
        </div>
      </div>

      <div class="table-content-area">
        <!-- Spinner -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-success" role="status"></div>
          <p class="text-muted mt-2">Chargement des factures d'achat...</p>
        </div>

        <!-- Etat vide (Exactly as in reference image) -->
        <div v-else-if="filteredFactures.length === 0" class="empty-state-wrapper py-5 text-center">
          <div class="empty-box-icon mb-3">
            <i class="fas fa-inbox"></i>
          </div>
          <h5 class="empty-state-title">Aucune facture trouvée</h5>
          <p class="empty-state-desc">Aucune facture d'achat ne correspond à vos critères.</p>
          <button class="btn-create-facture mt-3" @click="showCreateModal = true">
            <i class="fas fa-plus me-2"></i>Nouvelle facture d'achat
          </button>
        </div>

        <!-- Tableau des données -->
        <div v-else class="table-responsive">
          <table class="table-custom">
            <thead>
              <tr>
                <th @click="sortBy('numeroFactureFournisseur')" class="sortable">N° Facture <i class="fas fa-sort text-muted ms-1"></i></th>
                <th @click="sortBy('fournisseur')" class="sortable">Fournisseur <i class="fas fa-sort text-muted ms-1"></i></th>
                <th @click="sortBy('dateFacture')" class="sortable">Date <i class="fas fa-sort text-muted ms-1"></i></th>
                <th @click="sortBy('montantTotalHT')" class="sortable text-end">HT <i class="fas fa-sort text-muted ms-1"></i></th>
                <th @click="sortBy('montantTotalTVA')" class="sortable text-end">TVA <i class="fas fa-sort text-muted ms-1"></i></th>
                <th @click="sortBy('montantTotalTTC')" class="sortable text-end">TTC <i class="fas fa-sort text-muted ms-1"></i></th>
                <th class="text-center">Statut</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in filteredFactures" :key="f._id">
                <td class="fw-bold text-dark">{{ f.numeroFactureFournisseur || f.numeroEntree }}</td>
                <td>
                  <div class="fw-bold text-dark">{{ f.fournisseur }}</div>
                  <small class="text-muted" v-if="f.matriculeFiscal">MF: {{ f.matriculeFiscal }}</small>
                </td>
                <td class="text-muted">{{ formatDate(f.dateFacture) }}</td>
                <td class="text-end fw-semibold">{{ (f.montantTotalHT || 0).toFixed(2).replace('.', ',') }} DT</td>
                <td class="text-end text-muted">{{ (f.montantTotalTVA || 0).toFixed(2).replace('.', ',') }} DT</td>
                <td class="text-end fw-bold text-dark">{{ (f.montantTotalTTC || 0).toFixed(2).replace('.', ',') }} DT</td>
                <td class="text-center">
                  <span class="status-pill-badge" :class="getStatutPillClass(f.statut)">
                    {{ f.statut }}
                  </span>
                </td>
                <td class="text-center">
                  <button class="btn-action-icon me-1" title="Voir détails" @click="openDetailModal(f)">
                    <i class="fas fa-eye"></i>
                  </button>
                  <button
                    v-if="f.statut !== 'Annulée' && isAdmin"
                    class="btn-action-icon delete"
                    title="Annuler"
                    @click="confirmAnnuler(f)"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <FactureAchatModal
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="onFactureCreated"
    />

    <FactureAchatDetailModal
      :show="showDetailModal"
      :facture="selectedFacture"
      @close="showDetailModal = false"
    />
  </div>
</template>

<script>
import achatsService from '@/services/achatsService';
import FactureAchatModal from '@/components/achats/FactureAchatModal.vue';
import FactureAchatDetailModal from '@/components/achats/FactureAchatDetailModal.vue';

export default {
  name: 'Achats',
  components: {
    FactureAchatModal,
    FactureAchatDetailModal
  },
  data() {
    return {
      loading: false,
      factures: [],
      selectedFacture: null,
      showCreateModal: false,
      showDetailModal: false,
      currentTab: 'Toutes',
      kpis: {
        totalAchatsMoisTTC: 0,
        totalAchatsMoisHT: 0,
        nombreFacturesMois: 0,
        totalToutesFactures: 0,
        nombreFournisseurs: 0
      },
      filters: {
        search: '',
        fournisseur: '',
        dateDebut: '',
        dateFin: ''
      },
      sortKey: '',
      sortAsc: true
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.getters['auth/user'];
      return user && user.role === 'Admin';
    },
    uniqueFournisseurs() {
      return Array.from(new Set(this.factures.map(f => f.fournisseur).filter(Boolean)));
    },
    totalCount() {
      return this.factures.length;
    },
    brouillonsCount() {
      return this.factures.filter(f => f.statut === 'Brouillon').length;
    },
    valideesCount() {
      return this.factures.filter(f => f.statut === 'Validée').length;
    },
    payeesCount() {
      return this.factures.filter(f => f.statutPaiement === 'paye').length;
    },
    impayeesCount() {
      return this.factures.filter(f => f.statutPaiement === 'non_paye').length;
    },
    annuleesCount() {
      return this.factures.filter(f => f.statut === 'Annulée').length;
    },
    filteredFactures() {
      let result = [...this.factures];

      if (this.currentTab === 'Brouillons') result = result.filter(f => f.statut === 'Brouillon');
      if (this.currentTab === 'Validées') result = result.filter(f => f.statut === 'Validée');
      if (this.currentTab === 'Payées') result = result.filter(f => f.statutPaiement === 'paye');
      if (this.currentTab === 'Impayées') result = result.filter(f => f.statutPaiement === 'non_paye');
      if (this.currentTab === 'Annulées') result = result.filter(f => f.statut === 'Annulée');

      if (this.filters.search) {
        const q = this.filters.search.toLowerCase();
        result = result.filter(f =>
          (f.numeroFactureFournisseur && f.numeroFactureFournisseur.toLowerCase().includes(q)) ||
          (f.numeroEntree && f.numeroEntree.toLowerCase().includes(q)) ||
          (f.fournisseur && f.fournisseur.toLowerCase().includes(q))
        );
      }

      if (this.filters.fournisseur) {
        result = result.filter(f => f.fournisseur === this.filters.fournisseur);
      }

      if (this.sortKey) {
        result.sort((a, b) => {
          let valA = a[this.sortKey] || '';
          let valB = b[this.sortKey] || '';
          if (valA < valB) return this.sortAsc ? -1 : 1;
          if (valA > valB) return this.sortAsc ? 1 : -1;
          return 0;
        });
      }

      return result;
    }
  },
  mounted() {
    this.fetchKPIs();
    this.fetchFactures();
  },
  methods: {
    async fetchKPIs() {
      try {
        const res = await achatsService.getKPIs();
        if (res.success && res.data) {
          this.kpis = res.data;
        }
      } catch (err) {
        console.error('Erreur chargement KPIs achats:', err);
      }
    },
    async fetchFactures() {
      try {
        this.loading = true;
        const res = await achatsService.getFactures({ limit: 100 });
        if (res.success && res.data) {
          this.factures = res.data.docs || res.data;
        }
      } catch (err) {
        console.error('Erreur chargement factures:', err);
      } finally {
        this.loading = false;
      }
    },
    selectTab(tabName) {
      this.currentTab = tabName;
    },
    resetFilters() {
      this.filters.search = '';
      this.filters.fournisseur = '';
      this.filters.dateDebut = '';
      this.filters.dateFin = '';
      this.currentTab = 'Toutes';
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    openDetailModal(f) {
      this.selectedFacture = f;
      this.showDetailModal = true;
    },
    onFactureCreated() {
      this.fetchKPIs();
      this.fetchFactures();
    },
    async confirmAnnuler(f) {
      if (confirm(`Êtes-vous sûr de vouloir annuler la facture N° ${f.numeroEntree} ?`)) {
        try {
          const res = await achatsService.annulerFacture(f._id);
          if (res.success) {
            this.$toast.success('Facture annulée avec succès.');
            this.fetchFactures();
          }
        } catch (err) {
          this.$toast.error('Erreur lors de l\'annulation');
        }
      }
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('fr-FR');
    },
    getStatutPillClass(statut) {
      switch (statut) {
        case 'Validée': return 'pill-green';
        case 'Brouillon': return 'pill-orange';
        case 'Annulée': return 'pill-red';
        default: return 'pill-gray';
      }
    },
    exportData() {
      alert('Exportation des factures d\'achat au format CSV/Excel...');
    }
  }
};
</script>

<style scoped>
.achats-view {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1e293b;
}

/* En-tête */
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-icon-badge {
  width: 46px;
  height: 46px;
  background-color: #e0f2fe;
  color: #0284c7;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.header-title {
  font-size: 1.45rem;
  font-weight: 700;
  color: #0f172a;
}

.header-subtitle {
  font-size: 0.88rem;
  color: #64748b;
}

.btn-create-facture {
  background-color: #10b981;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
  transition: all 0.2s ease;
}

.btn-create-facture:hover {
  background-color: #059669;
  transform: translateY(-1px);
}

/* KPIs Row (4 cartes blanches avec icônes colorées) */
.kpis-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
}

@media (max-width: 1100px) {
  .kpis-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpis-row {
    grid-template-columns: 1fr;
  }
}

.kpi-white-card {
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.kpi-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.icon-blue {
  background-color: #e0f2fe;
  color: #0284c7;
}

.icon-green {
  background-color: #dcfce7;
  color: #16a34a;
}

.icon-purple {
  background-color: #f3e8ff;
  color: #9333ea;
}

.icon-orange {
  background-color: #ffedd5;
  color: #ea580c;
}

.kpi-data {
  display: flex;
  flex-direction: column;
}

.kpi-label-muted {
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 500;
}

.kpi-main-number {
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0.2rem 0;
}

.kpi-growth-text {
  font-size: 0.76rem;
  color: #10b981;
  font-weight: 600;
}

.kpi-sub-text {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Barre des Filtres */
.filters-bar-card {
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: grid;
  grid-template-columns: 1.5fr 1.5fr 1fr 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

@media (max-width: 992px) {
  .filters-bar-card {
    grid-template-columns: 1fr 1fr;
  }
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.filter-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.85rem;
}

.form-control-pill,
.form-select-pill {
  width: 100%;
  padding: 0.55rem 0.85rem 0.55rem 2.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.88rem;
  color: #1e293b;
  background-color: #ffffff;
}

.form-select-pill {
  padding-left: 0.85rem;
}

.form-control-pill:focus,
.form-select-pill:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.btn-reset-align {
  display: flex;
  align-items: flex-end;
}

.btn-reset-pill {
  padding: 0.55rem 1.1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #475569;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-reset-pill:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

/* Onglets des Statuts */
.status-tabs-row {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.tab-pill {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.5rem 1rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.tab-pill:hover {
  border-color: #cbd5e1;
}

.tab-pill.active {
  background-color: #dcfce7;
  color: #15803d;
  border-color: #86efac;
}

.tab-badge {
  padding: 0.15rem 0.55rem;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
}

.badge-green-light {
  background-color: #16a34a;
  color: white;
}

.badge-gray {
  background-color: #e2e8f0;
  color: #475569;
}

.badge-green {
  background-color: #dcfce7;
  color: #15803d;
}

.badge-blue {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.badge-orange {
  background-color: #ffedd5;
  color: #c2410c;
}

.badge-red {
  background-color: #fee2e2;
  color: #b91c1c;
}

/* Main Table Card */
.main-table-card {
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.table-card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.btn-exporter {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}

/* Table Custom */
.table-custom {
  width: 100%;
  border-collapse: collapse;
}

.table-custom th {
  padding: 0.85rem 1.25rem;
  background-color: #f8fafc;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  border-bottom: 1px solid #e2e8f0;
}

.table-custom th.sortable {
  cursor: pointer;
}

.table-custom td {
  padding: 0.95rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.88rem;
}

/* Etat Vide */
.empty-box-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #f1f5f9;
  color: #64748b;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-title {
  font-weight: 700;
  color: #0f172a;
}

.empty-state-desc {
  color: #64748b;
  font-size: 0.88rem;
}

/* Status Pills */
.status-pill-badge {
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
}

.pill-green {
  background-color: #dcfce7;
  color: #15803d;
}

.pill-orange {
  background-color: #ffedd5;
  color: #c2410c;
}

.pill-red {
  background-color: #fee2e2;
  color: #b91c1c;
}

.pill-gray {
  background-color: #f1f5f9;
  color: #475569;
}

.btn-action-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #0284c7;
  cursor: pointer;
}

.btn-action-icon.delete {
  color: #ef4444;
}
</style>
